import { isSoftSlot, type ClueSlot } from './types';

/**
 * Das feste Erzaehlskelett.
 *
 * Handgeschrieben und ueber alle Durchlaeufe identisch — es bestimmt Pacing und
 * Dramaturgie. Nur der Inhalt der Slots wechselt.
 *
 * Der Aufbau folgt einer Regel, die den ganzen Fall traegt:
 *
 *   Akt I und II bestehen fast nur aus **weichen** Slots — Zeugenaussagen und
 *   Instrumentenanzeigen, also Dinge, die man ehrlich verschieden deuten kann.
 *   Hier duerfen falsche Faehrten stehen.
 *
 *   Akt III gehoert vollstaendig der Wahrheit und bringt die **harten** Slots:
 *   Fundstuecke, Spuren der Vermissten, Belege fuer Anwesende. Erst hier faellt
 *   die Entscheidung.
 *
 * Der einzige harte Slot vor Akt III steht am Ende von Akt II — der Moment, in
 * dem der Fall kippt.
 */

export type Act = 1 | 2 | 3;

export interface SkeletonBeat {
  readonly index: number;
  readonly act: Act;
  readonly slot: ClueSlot;
  /** Nur die Wahrheit darf diesen Beat belegen. */
  readonly truthOnly: boolean;
}

const LAYOUT: readonly { act: Act; slot: ClueSlot }[] = [
  // Akt I — Etwas stimmt nicht, aber niemand weiss, was.
  { act: 1, slot: 'witness_testimony' },
  { act: 1, slot: 'instrument_reading' },
  { act: 1, slot: 'anomaly_signature' },
  { act: 1, slot: 'witness_testimony' },

  // Akt II — Die Hinweise haeufen sich und widersprechen einander.
  { act: 2, slot: 'instrument_reading' },
  { act: 2, slot: 'anomaly_signature' },
  { act: 2, slot: 'witness_testimony' },
  { act: 2, slot: 'instrument_reading' },
  // Der Kipppunkt: zum ersten Mal ein harter Beleg.
  { act: 2, slot: 'third_party_interference' },

  // Akt III — Die Wahrheit, Stueck fuer Stueck.
  { act: 3, slot: 'anomaly_signature' },
  { act: 3, slot: 'physical_artifact' },
  { act: 3, slot: 'missing_person_trace' },
  { act: 3, slot: 'physical_artifact' },
  { act: 3, slot: 'third_party_interference' },
];

export const SKELETON: readonly SkeletonBeat[] = LAYOUT.map((entry, index) => ({
  index,
  act: entry.act,
  slot: entry.slot,
  // Harte Slots und alles ab Akt III gehoeren ausschliesslich der Wahrheit.
  truthOnly: entry.act === 3 || !isSoftSlot(entry.slot),
}));

export const TOTAL_BEATS = SKELETON.length;

/** Beats, die eine falsche Faehrte tragen duerfen. */
export const DECOY_ELIGIBLE_BEATS = SKELETON.filter((beat) => !beat.truthOnly);

export function beatsInAct(act: Act): readonly SkeletonBeat[] {
  return SKELETON.filter((beat) => beat.act === act);
}

/** Index des ersten Beats, der ausschliesslich der Wahrheit gehoert. */
export const FIRST_TRUTH_ONLY_INDEX = SKELETON.findIndex((beat) => beat.truthOnly);
